import React from "react";

import { Config } from "../Config/Config";

type LocaleStrings = {
  formInputPlaceholder: string;
  formSendButton: string;
  multiSelectSubmit: string;
  loadPastMessages: string;
  loadingPastMessages: string;
  noRecentMessages: string;
  locationPickerLocationsWithin: string;
  locationPickerMilesOf: string;
  locationPickerKmOf: string;
  locationPickerLocationsNear: string;
  locationPickerSearch: string;
  locationPickerMilesOfPostalCode: string;
  locationPickerKmOfPostalCode: string;
  locationPickerSearchButton: string;
  locationPickerViewMap: string;
  locationPickerNoResults: string;
  inventoryPickerCantFind: string;
  inventoryPickerNoMatches: string;
  inventoryPickerRefineSearch: string;
  inventoryPickerSearchAnotherStore: string;
  inventoryPickerEditFilters: string;
  inventoryPickerSpacesAvailable: string;
  inventoryPickerFilter: string;
  inventoryPickerFilters: string;
  inventoryPickerAvailableUnitSizes: string;
  inventoryPickerUnitType: string;
  inventoryPickerResetFilters: string;
  inventoryPickerSave: string;
  inventoryPickerFloor: string;
  inventoryPickerFrom: string;
  profileCollectOptional: string;
  profileCollectCancel: string;
  profileCollectCanceled: string;
  profileCollectInstruction: string;
  profileCollectErrorRequired: string;
  profileCollectErrorInvalidEmail: string;
  profileCollectErrorInvalidPhone: string;
  profileCollectErrorInvalidPostalCode: string;
  profileCollectErrorNameTooShort: string;
};

export type { LocaleStrings };

type SupportedLocale = "en" | "es" | "fr";

const strings: Record<SupportedLocale, LocaleStrings> = {
  en: {
    formInputPlaceholder: "Enter your message...",
    formSendButton: "Send",
    multiSelectSubmit: "SUBMIT",
    loadPastMessages: "Load Past Messages",
    loadingPastMessages: "Loading Past Messages",
    noRecentMessages: "No recent messages.",
    locationPickerLocationsWithin: "Locations within",
    locationPickerMilesOf: "miles of",
    locationPickerKmOf: "km of",
    locationPickerLocationsNear: "Locations Near",
    locationPickerSearch: "Search",
    locationPickerMilesOfPostalCode: "miles of postal code",
    locationPickerKmOfPostalCode: "km of postal code",
    locationPickerSearchButton: "SEARCH",
    locationPickerViewMap: "View Map",
    locationPickerNoResults: "Oops! We didn't find any locations near you",
    inventoryPickerCantFind: "Can't find what you're looking for?",
    inventoryPickerNoMatches: "We didn't find any matching units",
    inventoryPickerRefineSearch: "Use the buttons below to refine your search",
    inventoryPickerSearchAnotherStore: "Search Another Store",
    inventoryPickerEditFilters: "Edit Filters",
    inventoryPickerSpacesAvailable: "Spaces Available",
    inventoryPickerFilter: "FILTER",
    inventoryPickerFilters: "FILTERS",
    inventoryPickerAvailableUnitSizes: "Available Unit Sizes",
    inventoryPickerUnitType: "Unit Type",
    inventoryPickerResetFilters: "Reset Filters",
    inventoryPickerSave: "Save",
    inventoryPickerFloor: "Floor",
    inventoryPickerFrom: "From",
    profileCollectOptional: "(optional)",
    profileCollectCancel: "Cancel",
    profileCollectCanceled: "Canceled",
    profileCollectInstruction: "Please provide your information to continue",
    profileCollectErrorRequired: "* required",
    profileCollectErrorInvalidEmail: "* invalid email",
    profileCollectErrorInvalidPhone: "* invalid phone number",
    profileCollectErrorInvalidPostalCode: "* invalid code",
    profileCollectErrorNameTooShort: "* name must be at least 2 characters"
  },
  es: {
    formInputPlaceholder: "Escribe tu mensaje...",
    formSendButton: "Enviar",
    multiSelectSubmit: "ENTREGAR",
    loadPastMessages: "Cargar mensajes anteriores",
    loadingPastMessages: "Cargando mensajes anteriores",
    noRecentMessages: "No hay mensajes recientes.",
    locationPickerLocationsWithin: "Ubicaciones a menos de",
    locationPickerMilesOf: "millas de",
    locationPickerKmOf: "km de",
    locationPickerLocationsNear: "Ubicaciones cerca de",
    locationPickerSearch: "Buscar",
    locationPickerMilesOfPostalCode: "millas del código postal",
    locationPickerKmOfPostalCode: "km del código postal",
    locationPickerSearchButton: "BUSCAR",
    locationPickerViewMap: "Ver mapa",
    locationPickerNoResults: "Vaya, no encontramos ubicaciones cerca de ti",
    inventoryPickerCantFind: "¿No encuentras lo que buscas?",
    inventoryPickerNoMatches: "No encontramos unidades que coincidan",
    inventoryPickerRefineSearch: "Usa los botones de abajo para refinar tu búsqueda",
    inventoryPickerSearchAnotherStore: "Buscar otra tienda",
    inventoryPickerEditFilters: "Editar filtros",
    inventoryPickerSpacesAvailable: "Espacios disponibles",
    inventoryPickerFilter: "FILTRAR",
    inventoryPickerFilters: "FILTROS",
    inventoryPickerAvailableUnitSizes: "Tamaños disponibles",
    inventoryPickerUnitType: "Tipo de unidad",
    inventoryPickerResetFilters: "Restablecer filtros",
    inventoryPickerSave: "Guardar",
    inventoryPickerFloor: "Piso",
    inventoryPickerFrom: "Desde",
    profileCollectOptional: "(opcional)",
    profileCollectCancel: "Cancelar",
    profileCollectCanceled: "Cancelado",
    profileCollectInstruction: "Proporciona tu información para continuar",
    profileCollectErrorRequired: "* obligatorio",
    profileCollectErrorInvalidEmail: "* correo no válido",
    profileCollectErrorInvalidPhone: "* número de teléfono no válido",
    profileCollectErrorInvalidPostalCode: "* código postal no válido",
    profileCollectErrorNameTooShort: "* el nombre debe tener al menos 2 caracteres"
  },
  fr: {
    formInputPlaceholder: "Entrez votre message...",
    formSendButton: "Envoyer",
    multiSelectSubmit: "LIVRER",
    loadPastMessages: "Charger les messages précédents",
    loadingPastMessages: "Chargement des messages précédents",
    noRecentMessages: "Aucun message récent.",
    locationPickerLocationsWithin: "Emplacements dans un rayon de",
    locationPickerMilesOf: "miles de",
    locationPickerKmOf: "km de",
    locationPickerLocationsNear: "Emplacements près de",
    locationPickerSearch: "Rechercher",
    locationPickerMilesOfPostalCode: "miles de code postal",
    locationPickerKmOfPostalCode: "km du code postal",
    locationPickerSearchButton: "RECHERCHER",
    locationPickerViewMap: "Voir la carte",
    locationPickerNoResults: "Désolé, aucun emplacement trouvé près de chez vous",
    inventoryPickerCantFind: "Vous ne trouvez pas ce que vous cherchez ?",
    inventoryPickerNoMatches: "Aucune unité correspondante trouvée",
    inventoryPickerRefineSearch: "Utilisez les boutons ci-dessous pour affiner votre recherche",
    inventoryPickerSearchAnotherStore: "Rechercher un autre magasin",
    inventoryPickerEditFilters: "Modifier les filtres",
    inventoryPickerSpacesAvailable: "Espaces disponibles",
    inventoryPickerFilter: "FILTRER",
    inventoryPickerFilters: "FILTRES",
    inventoryPickerAvailableUnitSizes: "Tailles disponibles",
    inventoryPickerUnitType: "Type d'unité",
    inventoryPickerResetFilters: "Réinitialiser les filtres",
    inventoryPickerSave: "Enregistrer",
    inventoryPickerFloor: "Niveau",
    inventoryPickerFrom: "À partir de",
    profileCollectOptional: "(facultatif)",
    profileCollectCancel: "Annuler",
    profileCollectCanceled: "Annulé",
    profileCollectInstruction: "Veuillez fournir vos informations afin de continuer",
    profileCollectErrorRequired: "* obligatoire",
    profileCollectErrorInvalidEmail: "* adresse courriel invalide",
    profileCollectErrorInvalidPhone: "* numéro de téléphone invalide",
    profileCollectErrorInvalidPostalCode: "* code postal invalide",
    profileCollectErrorNameTooShort: "* le nom doit comporter au moins 2 caractères"
  }
};

const LANGUAGE_STORAGE_KEY_PREFIX = "swivl_language_";

function resolveLocale(locale?: string): SupportedLocale {
  const code = (locale || Config.language || "en").toLowerCase();
  return (code in strings ? code : "en") as SupportedLocale;
}

export function t(key: keyof LocaleStrings, locale?: string): string {
  return strings[resolveLocale(locale)][key];
}

export function getPreloadLanguage(): string | undefined {
  return Config.preloadVariables?.["User:Language"];
}

export function getBrowserPreloadLanguage(): "es" | "fr" | undefined {
  if (typeof navigator === "undefined") {
    return undefined;
  }
  const raw = navigator.language || (navigator as any).userLanguage;

  if (!raw) {
    return undefined;
  }
  const primary = raw.toLowerCase().split("-")[0];

  if (primary === "es" || primary === "fr") {
    return primary;
  }
  return undefined;
}

export function resolveInitialLanguage(): string {
  const cached = localStorage ? localStorage.getItem(LANGUAGE_STORAGE_KEY_PREFIX + Config.orgId) : null;
  return (getPreloadLanguage() || cached || Config.language || "en").toLowerCase();
}

export function persistLanguage(language: string): void {
  if (localStorage) {
    localStorage.setItem(LANGUAGE_STORAGE_KEY_PREFIX + Config.orgId, language);
  }
}

export const LanguageContext = React.createContext<string>(getBrowserPreloadLanguage() || "en");
